import { useEffect, useState } from 'react';
import {
  Stack,
  Text,
  Group,
  Box,
  Badge,
  Button,
  Textarea,
  Loader,
} from '@mantine/core';
import { Grid, FileText, Target } from 'react-feather';
import type {
  ActiveSessionParticipant,
  ActiveSessionSlideDeck,
} from '../../../../services/api/coachActiveSession';
import type { AuthoredAgendaSectionForCoach } from '../../../../services/api/coachSessionPrep';
import { participantActiveSessionApi } from '../../../../services/api/participantActiveSession';
import { useSlideDeckDoc } from '../../../../components/common/slides/useSlideDeckDoc';
import { SectionSlides } from '../../../../components/common/slides/SectionSlides';
import { RichTextContent } from '../../../../components/common/RichTextEditor';
import { ContentCard } from '../../../../components/common/ContentCard';

/**
 * MED-792 AC1 + AC2 / MED-1016 — center column "Session Whiteboard" mirror.
 *
 * The shared surface is a single blue "Session Whiteboard — shared with all"
 * card (Figma 2044:16928) holding only genuinely-shared content:
 *   • a dark slide surface with the section badge (top-left), the relocated
 *     participant prompt (MED-1013) above the slide, and the slide(s) rendered
 *     via SectionSlides (main slide + filmstrip selection) — or a quiet "no
 *     slide" placeholder; and
 *   • a "Document" sub-card referencing the shared deck.
 *
 * The Commitment card lives OUTSIDE the blue card (a commitment is per-person,
 * not "shared with all"), gated on commitment_tracking_enabled AND the section
 * flags. It's role-aware (AC4): a coach sees read-only descriptive placeholders;
 * a peer leader (is_session_leader) sees their OWN functional prior-commitment
 * review + entry, reusing the participant commitment data/API, labeled
 * "Only you can see this".
 *
 * Slides are sourced from the authored section visuals + the shared current_
 * section_index/current_slide_page, so every attendee sees the same slide.
 */

// Figma 2044:16928 design tokens.
const BLUE_BG = '#F0F5FB';
const BLUE_BORDER = '#B0CCE0';
const BLUE_HEADER_BG = 'rgba(235, 242, 250, 0.8)';
const BLUE_LABEL = '#3B82B0';
const SURFACE_BG = '#1B2B3A';
const SUBCARD_BORDER = '#E5E7EB';
const MUTED = '#9CA3AF';
const COMMIT_HEADER_ICON_COLOR = '#6B7280';

// White text on the dark slide surface: the RichTextContent CSS module colors
// every block with var(--medley-text-primary), so we re-point that variable
// rather than fighting per-element selectors.
const PROMPT_ON_DARK_STYLE = {
  maxWidth: 560,
  textAlign: 'center',
  '--medley-text-primary': '#FFFFFF',
} as React.CSSProperties;

const COMMITMENT_BOX_STYLE: React.CSSProperties = {
  background: '#FAFBFC',
  border: '1px solid #E5E7EB',
  borderRadius: 8,
  padding: '12px 14px',
};

function CommitmentBoxLabel({ children }: { children: React.ReactNode }) {
  return (
    <Text
      fz={11}
      fw={700}
      c="dimmed"
      tt="uppercase"
      mb={6}
      style={{ letterSpacing: '0.4px' }}
    >
      {children}
    </Text>
  );
}

function CommitmentCardHeader({
  title,
  note,
}: {
  title: string;
  note?: string;
}) {
  return (
    <Group justify="space-between" align="center" wrap="nowrap" w="100%">
      <Group gap={6} align="center" wrap="nowrap">
        <Target
          size={16}
          color={COMMIT_HEADER_ICON_COLOR}
          aria-hidden="true"
          style={{ position: 'relative', top: -1 }}
        />
        <Text fz={12} fw={600} c="#374151">
          {title}
        </Text>
      </Group>
      {note && (
        <Text
          fz={10}
          fw={600}
          c="dimmed"
          tt="uppercase"
          style={{ letterSpacing: '0.4px' }}
          data-testid="active-session-whiteboard-commitment-private"
        >
          {note}
        </Text>
      )}
    </Group>
  );
}

/** MED-1016 AC4 — read-only descriptive placeholder shown to the coach (the
 *  commitment itself is per-participant and entered on their own screens). */
function CommitmentPlaceholder({
  label,
  body,
}: {
  label: string;
  body: string;
}) {
  return (
    <Box style={COMMITMENT_BOX_STYLE}>
      <CommitmentBoxLabel>{label}</CommitmentBoxLabel>
      <Text size="sm" c="dimmed" fs="italic">
        {body}
      </Text>
    </Box>
  );
}

/** MED-1016 AC4 — peer leader's own prior-session commitment (read-only). */
function LeaderPriorCommitment({ text }: { text: string | null }) {
  return (
    <Box
      data-testid="active-session-whiteboard-commitment-prior"
      style={COMMITMENT_BOX_STYLE}
    >
      <CommitmentBoxLabel>Last session&apos;s commitment</CommitmentBoxLabel>
      {text ? (
        <Text size="sm">{text}</Text>
      ) : (
        <Text size="sm" c="dimmed" fs="italic">
          No commitment recorded last session.
        </Text>
      )}
    </Box>
  );
}

/**
 * MED-1016 AC4 — peer leader's own commitment entry. Reuses the participant
 * commitment data/API (the leader is a participant); light-themed to fit the
 * coach Commitment card rather than the participant's dark slide card.
 */
function LeaderCommitmentEntry({ sessionId }: { sessionId: string }) {
  const [text, setText] = useState('');
  const [hydrated, setHydrated] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (hydrated) return;
    let cancelled = false;
    void (async () => {
      try {
        const result =
          await participantActiveSessionApi.getCommitment(sessionId);
        if (cancelled) return;
        setText(result.commitment_text ?? '');
        setSavedAt(result.updated_at ? new Date(result.updated_at) : null);
      } catch {
        // Non-blocking — the leader can still type and save.
      } finally {
        if (!cancelled) setHydrated(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [hydrated, sessionId]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const result = await participantActiveSessionApi.putCommitment(
        sessionId,
        text
      );
      setText(result.commitment_text ?? '');
      setSavedAt(result.updated_at ? new Date(result.updated_at) : new Date());
    } catch {
      setError('Could not save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box
      data-testid="active-session-whiteboard-commitment-entry"
      style={COMMITMENT_BOX_STYLE}
    >
      <CommitmentBoxLabel>This session&apos;s commitment</CommitmentBoxLabel>
      <Textarea
        value={text}
        onChange={e => setText(e.currentTarget.value)}
        placeholder="Write what you'll commit to."
        minRows={3}
        autosize
        maxRows={8}
        data-testid="active-session-whiteboard-commitment-textarea"
      />
      <Group justify="space-between" align="center" wrap="nowrap" mt={8}>
        <Text size="xs" c={error ? 'red' : 'dimmed'}>
          {error
            ? error
            : savedAt
              ? `Saved ${savedAt.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}`
              : ''}
        </Text>
        <Button
          size="xs"
          onClick={handleSave}
          loading={saving}
          disabled={saving}
          data-testid="active-session-whiteboard-commitment-save"
        >
          Save
        </Button>
      </Group>
    </Box>
  );
}

/**
 * MED-1132 / MED-1284 — content-free commitment-progress summary for facilitators
 * (coach + peer leader). Both roles render this identically; the peer leader gets
 * it in ADDITION to their own commitment box (AC1/AC7).
 *
 * MED-1284 — the count is scoped to the SESSION'S GROUP ROSTER, not to who is
 * currently connected. `participants` IS that roster: the backend already scopes
 * it to the one group the session is for and excludes soft-deleted users, the
 * coach, and withdrawn participants (MED-1267). So:
 *   - denominator = roster size, fixed as members drop / rejoin (AC1, AC2).
 *   - numerator = every roster member with a saved commitment, regardless of
 *     presence — a saved commitment is a persisted fact and never un-counts
 *     itself when a heartbeat lapses (AC3). The peer leader counts on roster
 *     membership alone, not on their presence heartbeat (AC5).
 *
 * Presence is used ONLY to label the two not-yet-logged lists, never the count:
 *   - "Still reflecting" = in session AND not logged (AC6).
 *   - "Haven't logged, not in session" = not in session AND not logged (AC7).
 * Anyone who logged and then left keeps `has_logged_commitment`, so they stay in
 * the numerator and appear in neither list (AC8). Each list is omitted when empty
 * (AC9), and their union is exactly the roster members without a commitment — so
 * they always account for the gap between numerator and denominator (AC10).
 *
 * Because the lists are labelled by presence while the count is not, a flickering
 * heartbeat can move a name between the two lists without changing the count.
 * That is intended (AC11) — do NOT re-scope the count to presence to "fix" it.
 *
 * The summary refreshes on the facilitator's existing poll, so it updates live as
 * members save (AC13). Privacy: this only ever reads `display_name` +
 * `has_logged_commitment` — the backend never sends commitment text to a
 * facilitator (AC14). Presence surfaces (Participants panel dots, "· N in
 * session" header) are untouched by this change (AC16).
 */
function CommitmentProgressSummary({
  participants,
}: {
  participants: ActiveSessionParticipant[];
}) {
  const names = (roster: ActiveSessionParticipant[]) =>
    roster.map(p => p.display_name.trim()).filter(name => name.length > 0);

  // Roster-based count — never presence-scoped (AC1–AC3, AC5).
  const total = participants.length;
  const loggedCount = participants.filter(p => p.has_logged_commitment).length;
  const allLogged = total > 0 && loggedCount === total;

  // Presence only labels the not-yet-logged; the two lists' union is the count
  // gap (AC10). Logged-then-left members have has_logged_commitment, so the
  // `!p.has_logged_commitment` guard drops them from both lists (AC8).
  const stillReflecting = names(
    participants.filter(p => p.is_connected && !p.has_logged_commitment),
  );
  const notInSession = names(
    participants.filter(p => !p.is_connected && !p.has_logged_commitment),
  );

  let headline: string;
  if (total === 0) {
    headline = 'No participants in this session.';
  } else if (allLogged) {
    headline =
      total === 1
        ? 'The participant has logged their commitment.'
        : `All ${total} participants have logged their commitment.`;
  } else {
    const subject = total === 1 ? 'participant has' : 'participants have';
    headline = `${loggedCount} of ${total} ${subject} logged their commitment.`;
  }

  return (
    <Box
      data-testid="active-session-whiteboard-commitment-progress"
      style={COMMITMENT_BOX_STYLE}
    >
      <CommitmentBoxLabel>Commitment progress</CommitmentBoxLabel>
      <Text
        size="sm"
        c={allLogged ? 'medleyGreen' : undefined}
        fw={allLogged ? 600 : 400}
        data-testid="active-session-whiteboard-commitment-progress-count"
      >
        {headline}
      </Text>
      {stillReflecting.length > 0 && (
        <Text
          size="sm"
          c="dimmed"
          mt={4}
          data-testid="active-session-whiteboard-commitment-progress-reflecting"
        >
          {`Still reflecting: ${stillReflecting.join(', ')}`}
        </Text>
      )}
      {notInSession.length > 0 && (
        <Text
          size="sm"
          c="dimmed"
          mt={4}
          data-testid="active-session-whiteboard-commitment-progress-not-in-session"
        >
          {`Haven't logged, not in session: ${notInSession.join(', ')}`}
        </Text>
      )}
    </Box>
  );
}

export interface ActiveSessionWhiteboardPanelProps {
  sections: AuthoredAgendaSectionForCoach[];
  sectionIndex: number | null;
  slideDeck: ActiveSessionSlideDeck | null;
  /** Route params — needed to fetch the converted deck PDF for rendering. */
  cohortId: string | null;
  sessionId: string | null;
  /** Coach-selected "main" slide (1-based page); null ⇒ section's first slide. */
  currentSlidePage?: number | null;
  /**
   * When provided, the filmstrip is clickable so the coach can promote a slide
   * to the main position (broadcast to all). Omit for read-only surfaces.
   */
  onSelectSlide?: (slidePage: number) => void;
  /** MED-1016 — cohort commitment gate; with the section flags, drives the card. */
  commitmentTrackingEnabled?: boolean;
  /**
   * MED-1016 AC4 — true when the requesting user is the peer leader (a
   * participant). Drives the role-aware Commitment card: leaders get their own
   * functional review/entry; coaches get read-only placeholders.
   */
  isSessionLeader?: boolean;
  /** MED-1016 AC4 — the requesting leader's prior-session commitment (null for coaches). */
  lastSessionCommitment?: string | null;
  /**
   * MED-1132 — live roster (connection + commitment-logged status) used to render
   * the facilitator commitment-progress summary. Same array the Participants panel
   * uses; refreshed on the existing ~5s poll so the summary updates live (AC5).
   */
  participants?: ActiveSessionParticipant[];
}

export function ActiveSessionWhiteboardPanel({
  sections,
  sectionIndex,
  slideDeck,
  cohortId,
  sessionId,
  currentSlidePage = null,
  onSelectSlide,
  commitmentTrackingEnabled = false,
  isSessionLeader = false,
  lastSessionCommitment = null,
  participants = [],
}: ActiveSessionWhiteboardPanelProps) {
  const section =
    sectionIndex !== null ? (sections[sectionIndex] ?? null) : null;
  const sectionTitle = section?.section_name?.trim() || null;
  const participantPrompt = section?.participant_prompt?.trim() || null;

  const visuals = section?.visuals ?? [];
  const hasVisuals = visuals.length > 0;

  // MED-1016 AC5 — Commitment card gates: cohort flag AND the section's flags.
  const showPriorCommitment =
    commitmentTrackingEnabled && !!section?.show_last_session_commitment;
  const showCommitmentEntry =
    commitmentTrackingEnabled && !!section?.show_commitment_field;
  const showCommitmentCard = showPriorCommitment || showCommitmentEntry;

  // Load the converted deck once it's ready, keyed on the deck (not the current
  // section's visuals) so the PDF persists as the leader advances sections.
  const deckReady = slideDeck?.processing_state === 'ready';
  const { doc, error } = useSlideDeckDoc({
    cohortId,
    sessionId,
    enabled: deckReady,
  });
  const slidesReady = hasVisuals && deckReady && !error && !!doc;
  const slidesLoading = hasVisuals && deckReady && !error && !doc;
  const hasSlideArea = slidesReady || slidesLoading;

  const sectionLabel = section
    ? `Section ${(sectionIndex ?? 0) + 1}${sectionTitle ? ` · ${sectionTitle}` : ''}`
    : null;

  return (
    <Stack gap="md" h="100%" data-testid="active-session-whiteboard-panel">
      {/* Blue "shared with all" card — the dark slide surface + document. */}
      <Box
        data-testid="active-session-whiteboard-shared"
        style={{
          background: BLUE_BG,
          border: `1px solid ${BLUE_BORDER}`,
          borderRadius: 12,
          overflow: 'hidden',
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header band. */}
        <Group
          gap={4}
          align="center"
          wrap="nowrap"
          style={{
            background: BLUE_HEADER_BG,
            borderBottom: `1px solid ${BLUE_BORDER}`,
            // Match the ContentCard panel header height (Notes/Participants/
            // Commitment) so all bordered headers line up.
            padding: '16px 16px',
          }}
        >
          <Grid size={14} color={BLUE_LABEL} aria-hidden="true" />
          <Text
            fz={11}
            fw={600}
            lh={1}
            c={BLUE_LABEL}
            tt="uppercase"
            // Optical nudge: uppercase caps sit slightly above the line-box
            // center, so even with lh=1 the label reads high beside the icon.
            style={{ letterSpacing: '0.44px', position: 'relative', top: 1 }}
          >
            Session Whiteboard — shared with all
          </Text>
        </Group>

        {/* Body. */}
        <Stack gap={16} style={{ padding: 16, flex: 1, minHeight: 0 }}>
          {/* Inner card: dark surface with badge + prompt + slide(s). */}
          <Box
            style={{
              background: '#FFFFFF',
              border: `1px solid ${SUBCARD_BORDER}`,
              borderRadius: 8,
              overflow: 'hidden',
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Box
              data-testid="active-session-whiteboard-surface"
              style={{
                flex: 1,
                minHeight: 320,
                display: 'flex',
                flexDirection: 'column',
                gap: 16,
                background: SURFACE_BG,
                padding: '20px 24px 24px',
              }}
            >
              {section && (
                <Badge
                  variant="filled"
                  color="medleyGreen"
                  size="sm"
                  radius="sm"
                  data-testid="active-session-whiteboard-section-badge"
                  style={{ alignSelf: 'flex-start' }}
                >
                  {sectionLabel}
                </Badge>
              )}

              {/* AC3 — prompt above the slide; standalone (centered) when the
                  section has no slide. */}
              <Box
                style={{
                  flex: 1,
                  minHeight: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 16,
                  alignItems: 'center',
                  justifyContent: hasSlideArea ? 'flex-start' : 'center',
                }}
              >
                {participantPrompt && (
                  <Box
                    data-testid="active-session-whiteboard-prompt"
                    style={PROMPT_ON_DARK_STYLE}
                  >
                    <RichTextContent content={participantPrompt} />
                  </Box>
                )}

                {slidesReady ? (
                  <SectionSlides
                    doc={doc!}
                    visuals={visuals}
                    selectedPage={currentSlidePage}
                    onSelectPage={onSelectSlide}
                  />
                ) : slidesLoading ? (
                  <Loader color="white" />
                ) : !participantPrompt ? (
                  <Text c="dimmed" ta="center" size="sm">
                    {section
                      ? 'No slide for this section.'
                      : 'No section is currently active.'}
                  </Text>
                ) : null}
              </Box>
            </Box>
          </Box>

          {/* Document sub-card. */}
          {slideDeck && (
            <Box
              data-testid="active-session-whiteboard-deck"
              style={{
                background: '#FFFFFF',
                border: `1px solid ${SUBCARD_BORDER}`,
                borderRadius: 8,
                padding: '11px 17px 15px',
              }}
            >
              <Stack gap={8}>
                <Group gap={4} align="center" wrap="nowrap">
                  <FileText
                    size={13}
                    color="#6B7280"
                    aria-hidden="true"
                    style={{ position: 'relative', top: -2 }}
                  />
                  <Text
                    fz={10}
                    fw={600}
                    c="#6B7280"
                    tt="uppercase"
                    style={{ letterSpacing: '0.4px' }}
                  >
                    {sectionTitle ? `Document · ${sectionTitle}` : 'Document'}
                  </Text>
                </Group>
                <Group gap={10} align="center" wrap="nowrap">
                  <Box
                    style={{
                      background: '#FEE2E2',
                      borderRadius: 6,
                      width: 32,
                      height: 32,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <FileText size={18} color="#DC2626" aria-hidden="true" />
                  </Box>
                  <Box style={{ minWidth: 0 }}>
                    <Text fz={13} c="#1A1A2E" truncate>
                      {slideDeck.original_filename}
                    </Text>
                    <Text fz={11} c={MUTED}>
                      Shared by Coach
                    </Text>
                  </Box>
                </Group>
              </Stack>
            </Box>
          )}
        </Stack>
      </Box>

      {/* Commitment (per-person, not "shared with all"). Role-aware (AC4):
          coach → read-only placeholders; peer leader → own functional
          review/entry, labeled private. */}
      {showCommitmentCard && (
        <ContentCard
          headerDivider
          fill={false}
          data-testid="active-session-whiteboard-commitment"
          header={
            <CommitmentCardHeader
              title="Commitment"
              note={isSessionLeader ? 'Only you can see this' : undefined}
            />
          }
        >
          <Stack gap="sm">
            {showPriorCommitment &&
              (isSessionLeader ? (
                <LeaderPriorCommitment text={lastSessionCommitment ?? null} />
              ) : (
                <CommitmentPlaceholder
                  label="Last session's commitment"
                  body="Participants are each being shown their personalized commitment from last session to review here."
                />
              ))}
            {showCommitmentEntry &&
              (isSessionLeader ? (
                sessionId ? (
                  <LeaderCommitmentEntry sessionId={sessionId} />
                ) : null
              ) : (
                <CommitmentPlaceholder
                  label="This session's commitment"
                  body="Participants have a field on their screen to enter or update their commitment for this session."
                />
              ))}
            {/* MED-1132 — live, content-free progress summary. Shown to both the
                coach and the peer leader (the latter in ADDITION to their own box
                above), wherever the commitment field is enabled (AC1/AC7/AC8). */}
            {showCommitmentEntry && (
              <CommitmentProgressSummary participants={participants} />
            )}
          </Stack>
        </ContentCard>
      )}
    </Stack>
  );
}
